import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import apiClient from '../services/api'

export interface SetupStatus {
  emailReady: boolean
  hasSalesProfile: boolean
  salesProfileCount: number
  isLoading: boolean
  hasError: boolean
  refetch: () => Promise<void>
}

const SUPPRESS_PATHS = ['/onboarding', '/login', '/signup', '/oauth']

export function useSetupStatus(): SetupStatus {
  const location = useLocation()
  const [emailReady, setEmailReady] = useState<boolean>(true)
  const [salesProfileCount, setSalesProfileCount] = useState<number>(1)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)

  const fetch = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)
    try {
      const [emailRes, gmailRes, profilesRes] = await Promise.allSettled([
        apiClient.get('/email-service-settings'),
        apiClient.get('/email-auth/gmail/status'),
        apiClient.get('/sales-profiles'),
      ])
      // /email-service-settings is admin-only and covers Postmark/Resend.
      // /email-auth/gmail/status works for any authenticated user.
      // Either one being ready is enough to send email.
      const postmarkOrResendReady =
        emailRes.status === 'fulfilled' && emailRes.value.data?.is_configured === true
      const gmailLinked =
        gmailRes.status === 'fulfilled' && gmailRes.value.data?.linked === true
      setEmailReady(postmarkOrResendReady || gmailLinked)
      const profiles =
        profilesRes.status === 'fulfilled' && Array.isArray(profilesRes.value.data)
          ? profilesRes.value.data
          : []
      setSalesProfileCount(profiles.length)
    } catch {
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const suppressed = SUPPRESS_PATHS.some(p => location.pathname.startsWith(p))
    if (suppressed) {
      setIsLoading(false)
      return
    }
    fetch()
  }, [location.pathname, fetch])

  return {
    emailReady,
    hasSalesProfile: salesProfileCount > 0,
    salesProfileCount,
    isLoading,
    hasError,
    refetch: fetch,
  }
}
